'use client';

import Close from '@hc-frontend/core-icons/kit/close';
import {
  ButtonClickEvent,
  DialogCloseButton,
  Link,
  useEventBus,
  useExperiments,
  useTrackingContexts,
} from '@hc-frontend/core-ui';
import { toUSPhone } from '@hc-frontend/core-utils-format';
import { CallCta } from '@hc-frontend/shells-shared';
import Image from 'next/image';
import { MouseEventHandler, ReactNode, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { IMask, IMaskInput } from 'react-imask';
import { z } from 'zod';

import { ConnectMeButton } from '../../atoms/connect-me-button';
import { useUccModalDataContext } from './modal-data-provider';
import { useModalCommonUtils } from './use-modal-common-utils';

const PhoneState = z.enum(['INITIAL', 'EDITING']);
type PhoneState = z.infer<typeof PhoneState>;

import { UsPhone } from '@hc-frontend/core-types';
import { zodResolver } from '@hookform/resolvers/zod';

import { AnimatedPhoneIcon } from '../../atoms/animated-phone-icon/animated-phone-icon';
import { Countdown } from '../../atoms/countdown/countdown';
import { TrustedForm } from '../../atoms/trusted-form/trusted-form';

export const FormInputs = z.object({
  phoneNumber: UsPhone,
});

type FormInputs = z.infer<typeof FormInputs>;

type UccModalContentCorrectPhoneProps = {
  showCountdown?: boolean;
  ctaText?: string;
};

export function UccModalContentCorrectPhone({
  showCountdown = true,
  ctaText = 'Call Now!',
}: UccModalContentCorrectPhoneProps) {
  const {
    id,
    phoneNumber,
    marketplaceStaticBaseUrl,
    primaryCampaign,
    secondaryCampaign,
    metricName,
    experience,
    sessionId,
    product,
  } = useUccModalDataContext();

  const {
    isOpen,
    isInitCall,
    internalRef,
    connectMeButtonId,
    clickToCallButtonId,
    clickToCallLinkId,
    connectMeDestinationPhoneNumber,
    handleOnClickBackdrop,
    handleOnClickClose,
    handleOnClickConnectMe,
    handleOnCountdownClose,
  } = useModalCommonUtils();

  const editPhoneButtonId = `${id}-edit-phone-button`;
  const savePhoneButtonId = `${id}-save-phone-button`;

  const eventBus = useEventBus();
  const contexts = useTrackingContexts();
  const { mp_2597_ucc_notification_dot, MP_2587_UCC_TEXT_COPY } =
    useExperiments();

  const [userPhone, setUserPhone] = useState(phoneNumber);
  const [phoneState, setPhoneState] = useState<PhoneState>(
    PhoneState.Enum.INITIAL,
  );
  const [trustedFormCertUrl, setTrustedFormCertUrl] = useState<string>();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onSubmit',
    resolver: zodResolver(FormInputs),
  });

  const { onChange, ref, ...inputProps } = register('phoneNumber');

  const onEdit: MouseEventHandler<HTMLButtonElement> = (event) => {
    setPhoneState(PhoneState.Enum.EDITING);

    eventBus.publish<ButtonClickEvent>({
      id: editPhoneButtonId,
      topic: 'core.ui.button.click',
      source: event.currentTarget,
      contexts,
    });
  };

  const onSave: MouseEventHandler<HTMLButtonElement> = (event) => {
    eventBus.publish<ButtonClickEvent>({
      id: savePhoneButtonId,
      topic: 'core.ui.button.click',
      source: event.currentTarget,
      contexts,
    });
  };

  const onSubmit: SubmitHandler<FormInputs> = ({ phoneNumber }) => {
    setUserPhone(phoneNumber);
    setPhoneState(PhoneState.Enum.INITIAL);
  };

  const onCertUrl = (certUrl: string) => {
    setTrustedFormCertUrl(certUrl);
  };

  const text_variants: Record<
    string,
    {
      title: string;
      subtitle_phone: ReactNode;
      subtitle_without_phone: ReactNode;
      cta_phone: string;
      cta_without_phone: string;
    }
  > = {
    control: {
      title: 'A licensed insurance agent just became available!',
      subtitle_phone: (
        <>
          Click <span className="font-bold">Connect Me!</span> to receive a
          call. Answer when your phone rings.
        </>
      ),
      subtitle_without_phone: (
        <>
          Click <span className="font-bold">{ctaText}</span> to speak with a
          licensed sales agent.{' '}
          {isOpen && showCountdown ? (
            <>
              Your spot will go to another caller in{' '}
              <span className="font-bold">
                <Countdown initialSeconds={45} onEnd={handleOnCountdownClose} />
              </span>{' '}
              seconds.
            </>
          ) : null}
        </>
      ),
      cta_phone: 'Connect Me!',
      cta_without_phone: ctaText,
    },
    v1: {
      title: 'You’re all done! - Ready to Talk?',
      subtitle_phone: (
        <>
          Tap <span className="font-bold">Connect Me!</span> and a licensed
          insurance agent will call to help you review options or enroll.
        </>
      ),
      subtitle_without_phone: (
        <>
          Click <span className="font-bold">{ctaText}</span> to reach a licensed
          insurance agent who’ll review options and help with enrollment.
        </>
      ),
      cta_phone: 'Connect Me!',
      cta_without_phone: ctaText,
    },
    v2: {
      title: 'Your profile is complete',
      subtitle_phone: (
        <>
          You’ve done the hard part. Tap{' '}
          <span className="font-bold">Connect Me!</span> and a licensed
          insurance agent will give you a call.
        </>
      ),
      subtitle_without_phone: (
        <>
          You’ve done the hard part. Click{' '}
          <span className="font-bold">{ctaText}</span> to speak with a licensed
          insurance agent.
        </>
      ),
      cta_phone: 'Connect Me!',
      cta_without_phone: ctaText,
    },
    v3: {
      title: 'You’re all set — connect with a licensed insurance agent',
      subtitle_phone: (
        <>
          Your answers are ready. Tap{' '}
          <span className="font-bold">Connect Me!</span> and a licensed
          insurance agent will call, explain options, and help you enroll.
        </>
      ),
      subtitle_without_phone: (
        <>
          Your answers are ready. Click{' '}
          <span className="font-bold">{ctaText}</span> to talk with a licensed
          insurance agent who can explain options and help you enroll..
        </>
      ),
      cta_phone: 'Connect Me!',
      cta_without_phone: ctaText,
    },
  };

  const text =
    text_variants[MP_2587_UCC_TEXT_COPY ?? 'control'] || text_variants.control;

  return (
    <div className="hc-imposter-layout">
      <dialog
        aria-modal="true"
        ref={internalRef}
        onClick={handleOnClickBackdrop}
        className="max-w-[500px] overflow-visible rounded-lg bg-gray-0 shadow-lg backdrop:bg-[rgba(0,0,0,0.65)]"
      >
        <div className="relative flex w-full justify-center overflow-visible">
          <div className="absolute left-1/2 top-0 aspect-square w-full max-w-[300px] -translate-x-1/2 -translate-y-1/2 tablet:max-w-[380px]">
            <Image
              src={`${marketplaceStaticBaseUrl}/marketplace/images/${mp_2597_ucc_notification_dot === 'variant' ? 'call-licensed-agent-woman-dot.png' : 'call-licensed-agent-woman.png'}`}
              alt="Speak to a licensed agent"
              fill
            />
          </div>
          <div className="aspect-[3/1] w-full max-w-[300px] tablet:aspect-[2/1]" />

          <DialogCloseButton
            className="absolute right-16-16 top-16-16 outline-none"
            onClick={handleOnClickClose}
          >
            <Close className="size-16-16 text-gray-200" />
          </DialogCloseButton>
        </div>
        <div className="text-center text-text-950">
          <h1 className="mx-24-24 mt-32-32 hc-typography--heading-three mb-24-32 text-balance">
            {text.title}
          </h1>

          {userPhone ? (
            <>
              <div className="mb-16-24 mx-24-24">
                {!isInitCall ? (
                  <p
                    className="hc-typography--paragraph-md mb-24-24 text-balance"
                    data-testid="initial-connect-me-message"
                  >
                    {text.subtitle_phone}
                  </p>
                ) : null}

                <div
                  className={`${isInitCall ? 'border border-dashed border-gray-50 bg-primary-background-50 p-12-12' : ''} rounded-md`}
                >
                  <ConnectMeButton
                    className="w-full"
                    id={connectMeButtonId}
                    data-testid="typ-connect-me-button"
                    metricName={metricName}
                    trustedFormCertUrl={trustedFormCertUrl}
                    variant="primary-card"
                    type="button"
                    experience={experience}
                    phoneNumber={userPhone}
                    destinationPhoneNumber={connectMeDestinationPhoneNumber}
                    callType={primaryCampaign.callType}
                    placement={primaryCampaign.placement}
                    sessionId={sessionId}
                    product={product}
                    isSendingDisplayMetric={false}
                    onClick={handleOnClickConnectMe}
                    advertiser={primaryCampaign.advertiser}
                  >
                    <p className="flex gap-8-8 justify-center items-center mx-auto">
                      <AnimatedPhoneIcon />
                      <span className="mx-auto">{text.cta_phone}</span>
                    </p>
                  </ConnectMeButton>
                </div>

                {isInitCall && secondaryCampaign ? (
                  <p className="mt-8-8">
                    Or dial at{' '}
                    <Link
                      className="font-bold"
                      href={secondaryCampaign.href}
                      id={clickToCallLinkId}
                      data-call-call-type={secondaryCampaign.callType}
                      data-call-placement={secondaryCampaign.placement}
                      data-call-product={secondaryCampaign.product}
                      data-call-phone={secondaryCampaign.href}
                      data-call-advertiser={secondaryCampaign.advertiser}
                    >
                      {toUSPhone(secondaryCampaign.phoneNumber)}
                    </Link>
                  </p>
                ) : null}
              </div>

              {!isInitCall ? (
                <div className="px-32-32 py-16-16 bg-primary-background-50 rounded-b-lg">
                  {phoneState === PhoneState.Enum.INITIAL && (
                    <div
                      data-testid="initial-correct-phone-message"
                      className="text-center"
                    >
                      <p className="hc-typography--paragraph-sm text-balance text-center">
                        Is <span className="font-bold">{userPhone}</span> not
                        your phone?{' '}
                        <button
                          className="hc-typography--paragraph-sm underline text-tertiary-900 inline-block"
                          onClick={onEdit}
                        >
                          Edit
                        </button>
                      </p>
                    </div>
                  )}

                  {phoneState === PhoneState.Enum.EDITING && (
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <div className="hc-typography--paragraph-sm leading-tight flex flex-wrap gap-8-8 items-center justify-center">
                        <label
                          htmlFor="phoneNumber"
                          className="whitespace-nowrap font-bold"
                        >
                          Edit your phone:
                        </label>
                        <div className="flex gap-8-8 items-center justify-center">
                          <IMaskInput
                            {...inputProps}
                            id="phoneNumber"
                            inputRef={ref}
                            className="border rounded-md border-gray-100 bg-white px-8-8 py-4-4 w-[150px]"
                            onAccept={(_v, _r, e) => {
                              if (e) {
                                onChange(e);
                              }
                            }}
                            defaultValue={userPhone}
                            unmask={false}
                            mask={
                              new IMask.MaskedPattern({
                                mask: '(000) 000-0000',
                              })
                            }
                            lazy={false}
                            placeholder="(___) ___-____"
                            type="tel"
                          />

                          <button
                            type="submit"
                            className="hc-typography--paragraph-sm underline text-tertiary-900"
                            onClick={onSave}
                          >
                            Save
                          </button>
                        </div>
                      </div>

                      {errors.phoneNumber && (
                        <p className="hc-typography--help-text mt-12-12 text-error-800">
                          {errors.phoneNumber.message}
                        </p>
                      )}

                      <TrustedForm
                        id="UccTrustedFormScript"
                        testId="ucc-trusted-form"
                        onCertUrl={onCertUrl}
                      />
                    </form>
                  )}
                </div>
              ) : null}
            </>
          ) : (
            <div className="my-32-32 mx-24-24">
              <p className="hc-typography--paragraph-md mb-32-32 text-balance">
                {text.subtitle_without_phone}
              </p>

              {secondaryCampaign ? (
                <CallCta
                  id={clickToCallButtonId}
                  data-testid="typ-click-to-call-button"
                  phoneCampaign={secondaryCampaign}
                  className="flex w-full flex-wrap items-center justify-center gap-12-12"
                >
                  <p className="flex gap-8-8 justify-center items-center mx-auto">
                    <AnimatedPhoneIcon />
                    <span className="mx-auto">{text.cta_without_phone}</span>
                  </p>
                </CallCta>
              ) : null}

              {secondaryCampaign ? (
                <p className="mt-8-8">
                  Or dial at{' '}
                  <Link
                    className="font-bold"
                    href={secondaryCampaign.href}
                    id={clickToCallLinkId}
                    data-call-call-type={secondaryCampaign.callType}
                    data-call-placement={secondaryCampaign.placement}
                    data-call-product={secondaryCampaign.product}
                    data-call-phone={secondaryCampaign.href}
                    data-call-advertiser={secondaryCampaign.advertiser}
                  >
                    {toUSPhone(secondaryCampaign.phoneNumber)}
                  </Link>
                </p>
              ) : null}
            </div>
          )}
        </div>
      </dialog>
    </div>
  );
}
